<script lang="ts">
	import type { SvelteAsr, i18n } from 'types/common'
	import type { HomeTransferOrders$result, HomeData$result } from '$houdini'
	import type { TransferOrderFilters } from './transfer-order'
	import HomeFilter from '../HomeFilter.svelte'
	import { getDisplayStoreName } from '../home'
	import { toLocaleDateString } from '@isoftdata/utility-date-time'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import { booleanToYesNo } from '@isoftdata/utility-string'

	import Table, { type Column, Td } from '@isoftdata/svelte-table'
	import { getContext } from 'svelte'
	import NoItemsForSelectedFilter from 'components/NoItemsForSelectedFilter.svelte'

	interface Props {
		asr: SvelteAsr
		transferOrders: HomeTransferOrders$result['transferOrders']['items']
		stores: HomeData$result['stores']
		users: HomeData$result['userAccounts']
		userId: TransferOrderFilters['userId']
		storeId: TransferOrderFilters['storeId']
		showDoneReceiving: TransferOrderFilters['showDoneReceiving']
		dates: TransferOrderFilters['dates']
	}

	let {
		//
		asr,
		dates,
		transferOrders,
		stores,
		users,
		userId,
		storeId,
		showDoneReceiving,
	}: Props = $props()

	const { t: translate } = getContext<i18n>('i18next')

	let displayTransferOrders = $derived(
		transferOrders.map(to => {
			return {
				...to,
				source: getDisplayStoreName(to.sourceStore),
				destination: getDisplayStoreName(to.destinationStore),
				user: to.user?.name || '',
				transferOrderId: to.id,
				filled: to.doneReceiving,
			}
		}),
	)

	const columns: Array<Column<(typeof displayTransferOrders)[number]>> = [
		{
			property: 'transferOrderId',
			name: translate('home.transferOrder.transferOrderNumberColumn', 'TO #'),
			numeric: true,
		},
		{ property: 'date', name: translate('common:date', 'Date'), defaultSortColumn: true, defaultSortDirection: 'DESC' },
		{ property: 'source', name: translate('common:source', 'Source') },
		{ property: 'sourceComment', name: translate('common:sourceComment', 'Source Comment') },
		{ property: 'destination', name: translate('common:destination', 'Destination') },
		{ property: 'destinationComment', name: translate('common:destinationComment', 'Destination Comment') },
		{ property: 'user', name: translate('common:user', 'User') },
		{ property: 'ordered', name: translate('common:ordered', 'Ordered') },
		{ property: 'filled', name: translate('common:filled', 'Filled') },
	]

	function filterChange() {
		asr.go(
			null,
			{
				userId: userId || null, //undefined will cause the parameter default to kick in, so pass null instead
				storeId,
				showDoneReceiving,
				fromDate: dates?.from,
				toDate: dates?.to,
			},
			{ inherit: true },
		)
	}
</script>

<div class="row">
	<div class="col-md-3 col-lg-2">
		<HomeFilter
			bind:userId
			bind:storeId
			bind:dates
			{stores}
			{users}
			{filterChange}
			dateRangeSetting="defaultTransferOrderHistoryDateRange"
		>
			<Checkbox
				label={translate('showDoneReceiving', 'Show Done Receiving')}
				bind:checked={showDoneReceiving}
				onchange={filterChange}
			/>
		</HomeFilter>
	</div>

	<div
		class="col-md-9 col-lg-10 tab-pane"
		id="sales"
		role="tab"
	>
		{#if displayTransferOrders.length > 0}
			<Table
				responsive
				{columns}
				perPageCount={15}
				rows={displayTransferOrders}
				hover={false}
			>
				{#snippet children({ row })}
					<tr class:text-muted={row.doneReceiving}>
						<Td property="transferOrderId">{row.transferOrderId}</Td>
						<Td property="date">{toLocaleDateString(row.date)}</Td>
						<Td property="source">{row.source}</Td>
						<Td property="sourceComment">{row.sourceComment}</Td>
						<Td property="destination">{row.destination}</Td>
						<Td property="destinationComment">{row.destinationComment}</Td>
						<Td property="user">{row.user}</Td>
						<Td property="ordered">{booleanToYesNo(row.ordered)}</Td>
						<Td property="filled">{booleanToYesNo(row.filled)}</Td>
					</tr>
				{/snippet}
			</Table>
		{:else}
			<NoItemsForSelectedFilter />
		{/if}
	</div>
</div>
